/**
 * Strip HTML-like markup out of a one-line NAME — model, collection, product
 * name, series, variant label.
 *
 * Some shops wrap parts of a product title in theme markup that their feed
 * serves verbatim: Charlie Paris sends `<tc>ALLIANCE</tc> - <tc>Moon Phase</tc>
 * - Blue`, and the whole string, tags included, became the model, collection
 * and series. Descriptions were unaffected because they already pass through a
 * block-level HTML stripper; names never did.
 *
 * Rules:
 *  - Only things SHAPED like a tag are removed: `<` immediately followed by a
 *    letter (or `/` + letter) and closed by `>`. A bare `<` — "< 40mm", "A<B",
 *    "<3" — is left alone, which matters for the odd name that carries one.
 *  - The text inside a tag pair is kept: `<tc>ALLIANCE</tc>` → `ALLIANCE`.
 *  - `<br>` becomes a space rather than vanishing, so two words it separated do
 *    not fuse.
 *  - HTML entities are decoded once (`&amp;` → `&`), `&amp;` last so
 *    `&amp;lt;` does not become `<`.
 *  - Whitespace is collapsed and trimmed.
 *
 * This is for single-line names, not documents: it does not try to turn block
 * elements into line breaks. Use the description strippers for that.
 *
 * MIRRORED in functions/src/stripInlineMarkup.ts (the functions package cannot
 * import from src/). The two test files run the same cases; change both.
 */
const BREAK_TAG = /<br\s*\/?>/gi;
const ANY_TAG = /<\/?[a-zA-Z][a-zA-Z0-9:-]*(?:\s[^<>]*)?\/?>/g;

const NAMED_ENTITIES: Record<string, string> = {
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '\u2013',
  mdash: '\u2014',
  lsquo: '\u2018',
  rsquo: '\u2019',
  ldquo: '\u201C',
  rdquo: '\u201D',
  hellip: '\u2026',
  eacute: '\u00E9',
  egrave: '\u00E8',
  agrave: '\u00E0',
  ccedil: '\u00E7',
  uuml: '\u00FC',
  ouml: '\u00F6',
  auml: '\u00E4',
};

function decodeEntities(text: string): string {
  return text
    .replace(/&#(\d{1,7});/g, (m, dec: string) => {
      const code = Number(dec);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
    })
    .replace(/&#x([0-9a-f]{1,6});/gi, (m, hex: string) => {
      const code = parseInt(hex, 16);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
    })
    .replace(/&([a-zA-Z]{2,8});/g, (m, name: string) => NAMED_ENTITIES[name] ?? NAMED_ENTITIES[name.toLowerCase()] ?? m)
    .replace(/&amp;/gi, '&');
}

/** True when the text holds something {@link stripInlineMarkup} would change for being markup. */
export function hasInlineMarkup(text: unknown): boolean {
  if (typeof text !== 'string' || !text) return false;
  return stripInlineMarkup(text) !== text.replace(/\s+/g, ' ').trim();
}

export function stripInlineMarkup(text: string): string;
export function stripInlineMarkup(text: string | undefined | null): string | undefined;
export function stripInlineMarkup(text: string | undefined | null): string | undefined {
  if (typeof text !== 'string') return undefined;
  if (!text) return text;
  return decodeEntities(text.replace(BREAK_TAG, ' ').replace(ANY_TAG, ''))
    .replace(/\s+/g, ' ')
    .trim();
}
